/**
 * alertDetailsApi.js — API functions for the Alert Details Report page.
 *
 * fetchEventReport   — runs the full event report query
 * fetchFilterOptions — loads all 11 filter option sets
 * fetchPreformNotes  — preformatted note templates
 * addEventNote       — add note to event
 * editEventReason    — change event reason code
 */

import { apiFetch, apiTransaction, postReport } from '../../../../services/api.js';
import { markEventsChanged } from '../../../../services/reportCache.js';
import { reportFilterFields, responseTimeField, timeWindowField } from '../../../../utils/reportRequestBody.js';


// ---------------------------------------------------------------------------
// Fetch Event Report
// ---------------------------------------------------------------------------

/**
 * Runs the eventReport query with all filter fields.
 *
 * Uses a direct POST to the report endpoint because the V2 apiReport()
 * is missing fields required by EventReports (resident_group_filter,
 * area_group_filter, user_group_filter, resp_filter_items, username).
 *
 * @param {object} opts
 * @param {number} opts.campusId
 * @param {string} opts.username
 * @param {string} opts.startDate   — ISO format (yyyy-mm-dd hh:mm:ss)
 * @param {string} opts.endDate     — ISO format
 * @param {object} opts.filters     — all report-level filter selections
 * @returns {Promise<object>} — { records, summary, overlimit, url, ErrorCode }
 */
export async function fetchEventReport({ campusId, username, startDate, endDate, filters = {}, isCloud = false,
                                          isGrandstream = false }) {
    const body = {
        rep_type: 'eventReport',
        /* Selects which event proc the report Lambda runs: reason 0 is a real
           service period on a cloud campus and ambiguous on a non-cloud one, and
           on cloud an auto-cleared alert (-1) is not a response at all. See
           docs/REPORT_PROC_AUDIT_PLAN.md section 3.

           Load-bearing, not cosmetic: the Lambda defaults this to falsey, so if it
           is ever dropped a cloud campus silently gets the non-cloud proc -- a
           wrong number with no error. */
        is_cloud: isCloud ? 1 : 0,
        /* Adds time_to_connect / intercom_used to each row and the Time to Connect
           figures to the summary (docs/TIME_TO_CONNECT_PLAN.md). Omitted means no
           Time to Connect, nothing else changes. */
        is_grandstream: isGrandstream ? 1 : 0,
        username: username || '',
        campus_id: campusId,
        start_date: startDate,
        end_date: endDate,
        event_category_filter: '',
        ...reportFilterFields(filters),
        resp_filter_items: responseTimeField(filters),
        // Minutes from midnight, half-open [start, end). [0, 1440] is all day and
        // the query treats it as a no-op, so an omitted window changes nothing.
        time_window: timeWindowField(filters),
    };

    const res = await postReport(body, '[alertDetailsApi] fetchEventReport error');
    if (res.ErrorCode) return { ErrorCode: res.ErrorCode };
    const data = res.data;

    return {
        records: data?.records || [],
        summary: data?.summary || {},
        overlimit: Boolean(data?.overlimit),
        url: data?.url || '',
    };
}

// ---------------------------------------------------------------------------
// Fetch Filter Options
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Fetch Preformatted Notes
// ---------------------------------------------------------------------------

/**
 * @param {number} campusId
 * @returns {Promise<Array>}
 */
export async function fetchPreformNotes(campusId) {
    return apiFetch({
        table: 'core_preform_event_notes',
        procedure: 'fetchbyparameter',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

// ---------------------------------------------------------------------------
// Add Event Note
// ---------------------------------------------------------------------------

/**
 * @param {number} eventId
 * @param {number} campusId
 * @param {string} note
 * @param {string} userId
 * @param {string} clientId
 * @returns {Promise<[boolean, string?]>}
 */
/* /myrest -> mysql_transaction (docs/MYREST_MIGRATION_AUDIT.md section C): Admin+; notes must carry the request's campus. */
export async function addEventNote(eventId, campusId, note, userId, clientId) {
    /* Tell any cached report that contains these events that it is stale, so
       coming back to it shows this edit rather than the rows from before it.
       Marked on attempt, not on success: a false-positive stale costs
       one refresh of identical data, a missed one shows the user a
       report that contradicts the edit they just made.
       See docs/REPORT_STATE_RESTORE_PLAN.md O1. */
    markEventsChanged(eventId);
    return apiTransaction({
        transType: 'event_note_add',
        payload: [{ event_id: eventId, campus_id: campusId, note, modified_by: userId }],
        campusId,
        clientId,
        userId,
    });
}

// ---------------------------------------------------------------------------
// Edit Event Reason
// ---------------------------------------------------------------------------

/**
 * @param {number} eventId
 * @param {number} newReasonId
 * @param {number} oldReasonId
 * @param {number} campusId
 * @param {string} userId
 * @param {string} clientId
 * @returns {Promise<[boolean, string?]>}
 */
export async function editEventReason(eventId, newReasonId, oldReasonId, campusId, userId, clientId) {
    /* Tell any cached report that contains these events that it is stale, so
       coming back to it shows this edit rather than the rows from before it.
       Marked on attempt, not on success: a false-positive stale costs
       one refresh of identical data, a missed one shows the user a
       report that contradicts the edit they just made.
       See docs/REPORT_STATE_RESTORE_PLAN.md O1. */
    markEventsChanged(eventId);
    return apiTransaction({
        transType: 'event_reason_edit',
        payload: [{ event_id: eventId, reason_id: newReasonId }],
        oldPayload: [{ event_id: eventId, reason_id: oldReasonId }],
        campusId,
        userId,
        clientId,
    });
}

/** The full filter option set; see utils/reportFilterOptions.js (shared by every report page). */
export { fetchFilterOptions } from '../../../../utils/reportFilterOptions.js';
